import { forgetRecentLoginCodes } from '../api/backendApiClient.ts'
import { resetProgressiveWebApplication } from '../serviceWorker/pwaReset.ts'

/**
 * Empties everything this origin has kept in the browser and starts over on a blank one.
 *
 * `pwaReset()` answers one question — is a stale service worker serving me an old build —
 * and deliberately leaves the account book alone, because unregistering a worker is not a
 * reason to sign somebody out. This answers the other one: *what if nothing here were
 * remembered at all*. It clears web storage, IndexedDB and the cookies the page can see,
 * and then does everything `pwaReset()` does.
 *
 * That signs every account out **of this device**. The sessions themselves are untouched —
 * they live in PostgreSQL and last ten years — so this is the client-side act of leaving,
 * applied to every account at once. Nothing on the server is revoked, and a token that is
 * forgotten here still works elsewhere.
 *
 * One thing is genuinely beyond reach: a cookie marked `HttpOnly`, which no page can read
 * or delete. The only one this application ever sets is the ten-minute Google flow cookie,
 * and it clears itself.
 *
 * Development only, like `pwaReset()`. In production this would throw away somebody's
 * signed-in accounts and whatever they had cached to read offline.
 */
export async function resetApplicationState(): Promise<void> {
  clearWebStorage()
  clearCookiesVisibleToThisPage()
  await deleteIndexedDatabases()
  await forgetLoginCodesThePanelShows()

  // Reloads, so it goes last: everything above has to have happened by then.
  await resetProgressiveWebApplication()
}

/**
 * The one thing here that is not the browser's to forget.
 *
 * The login codes the panel lists are held by the running deployment, so clearing storage
 * cannot reach them, and leaving them would list yesterday's working credentials beside a
 * panel that had just been emptied.
 *
 * Failure is never fatal. Where the deployment did not turn the tools on the route does not
 * exist at all, which is a 404 rather than a problem, and nothing about forgetting what this
 * browser kept should depend on the server answering.
 */
async function forgetLoginCodesThePanelShows(): Promise<void> {
  try {
    await forgetRecentLoginCodes()
    console.info('appReset: cleared the login codes the development panel was showing')
  } catch (clearingFailure) {
    console.info('appReset: the development login codes could not be cleared', clearingFailure)
  }
}

function clearWebStorage(): void {
  // Private windows and browsers set to block site data throw on the accessor itself, so
  // reaching storage is attempted rather than assumed.
  for (const [storageName, storage] of [
    ['localStorage', window.localStorage],
    ['sessionStorage', window.sessionStorage],
  ] as const) {
    try {
      const clearedCount = storage.length
      storage.clear()
      console.info(`appReset: cleared ${clearedCount} ${storageName} key(s)`)
    } catch (storageFailure) {
      console.info(`appReset: ${storageName} could not be read`, storageFailure)
    }
  }
}

/**
 * Expires every cookie the page can see, at each path it could have been set for. A cookie
 * is deleted by being given a date in the past, and only for an exactly matching path, so
 * the path the page is on and every parent of it are all named.
 */
function clearCookiesVisibleToThisPage(): void {
  const cookieNames = document.cookie
    .split(';')
    .map((cookiePair) => cookiePair.split('=')[0]?.trim() ?? '')
    .filter((cookieName) => cookieName.length > 0)

  const pathsToClear = ['/', ...buildAncestorPaths(window.location.pathname)]
  for (const cookieName of cookieNames) {
    for (const cookiePath of pathsToClear) {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${cookiePath}`
    }
  }
  console.info(`appReset: expired ${cookieNames.length} readable cookie(s)`)
}

function buildAncestorPaths(currentPath: string): string[] {
  const pathSegments = currentPath.split('/').filter((pathSegment) => pathSegment.length > 0)
  return pathSegments.map(
    (_pathSegment, position) => `/${pathSegments.slice(0, position + 1).join('/')}`,
  )
}

async function deleteIndexedDatabases(): Promise<void> {
  // `databases()` is not implemented everywhere — Firefox has no way to enumerate them —
  // and there is nothing to fall back to, since a name nobody can list is a name nobody
  // can delete either.
  if (typeof indexedDB === 'undefined' || typeof indexedDB.databases !== 'function') {
    return
  }

  try {
    const databases = await indexedDB.databases()
    await Promise.all(
      databases.map(
        (database) =>
          new Promise<void>((resolveDeletion) => {
            if (database.name === undefined) {
              resolveDeletion()
              return
            }
            const deletionRequest = indexedDB.deleteDatabase(database.name)
            deletionRequest.onsuccess = () => resolveDeletion()
            deletionRequest.onerror = () => resolveDeletion()
            // A database still open in another tab blocks forever otherwise.
            deletionRequest.onblocked = () => resolveDeletion()
          }),
      ),
    )
    console.info(`appReset: deleted ${databases.length} IndexedDB database(s)`)
  } catch (databaseFailure) {
    console.info('appReset: IndexedDB could not be read', databaseFailure)
  }
}

declare global {
  interface Window {
    appReset?: () => Promise<void>
  }
}

export function installApplicationResetHelper(): void {
  window.appReset = resetApplicationState
  console.info(
    'appReset() is available: it clears storage, cookies and caches, signs every account out of this device and reloads.',
  )
}
